// src/pages/VehicleDetails.js
import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import PaymentCalculator from '../components/PaymentCalculator';
import { FaCar, FaFileContract, FaArrowLeft, FaHome, FaCheckCircle, FaClock } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

// Scroll Reveal Hook
const useScrollReveal = (threshold = 0.1, isReady = true) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!isReady || !ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold, isReady]);

  return [ref, isVisible];
};

const VehicleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState('');
  
  // Form States
  const [activeForm, setActiveForm] = useState(null); 
  const [formData, setFormData] = useState({
      fullName: '', email: '', phone: '', offerAmount: '', comments: '', 
      annualIncome: '', employmentStatus: '', creditScore: '', date: '', time: ''
  });
  const [formStatus, setFormStatus] = useState('');
  
  // Pre-Approval State
  const [approvalStatus, setApprovalStatus] = useState(null);

  // Current User
  const user = auth.currentUser;

  // --- Fetch Car Data ---
  useEffect(() => {
    const fetchCar = async () => {
      try {
        const docRef = doc(db, "cars", id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setCar({ id: docSnap.id, ...data });
          if (data.images && data.images.length > 0) {
            setSelectedImage(data.images[0]);
          } else {
            setSelectedImage(data.image || '');
          }
        } else {
          setError('Vehicle not found in database.');
        }
      } catch (err) {
        setError('Error loading vehicle details.');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchCar();
  }, [id]);

  // --- Fetch User's Pre-Approval Status for THIS Car ---
  useEffect(() => {
    const checkApprovalStatus = async () => {
      if (user && id) {
        try {
          const q = query(
            collection(db, "preApprovals"), 
            where("userId", "==", user.uid),
            where("carId", "==", id)
          );
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const data = querySnapshot.docs[0].data();
            setApprovalStatus(data.status);
          }
        } catch (error) {
          console.error("Error checking approval status:", error);
        }
      }
    };
    checkApprovalStatus();
  }, [user, id]);

  // --- Form Handlers ---
  const handleActionClick = (actionType) => {
    if (!user) {
        alert("Please login to access this feature.");
        navigate('/login');
        return;
    }
    if (car.status === 'Sold') return; 
    setActiveForm(activeForm === actionType ? null : actionType);
    setFormStatus('');
    // Pre-fill email/name from auth if available, but allow editing
    setFormData({
        fullName: user.displayName || '',
        email: user.email || '',
        phone: '',
        offerAmount: '', comments: '', 
        annualIncome: '', employmentStatus: '', creditScore: '', date: '', time: ''
    });
  };

  const handleFormChange = (e) => {
      setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e, collectionName) => {
      e.preventDefault();
      setFormStatus('Submitting...');
      
      const currencySymbol = car.currency || '$';

      try {
          const submissionData = {
              userId: user.uid,
              userEmail: user.email,
              carId: car.id,
              carName: `${car.year} ${car.make} ${car.model}`,
              ...formData,
              status: 'Pending', 
              createdAt: new Date()
          };

          // 1. Save to Firebase
          await addDoc(collection(db, collectionName), submissionData);
          
          // 2. Prepare Email Data
          let emailParams = {
              type: activeForm === 'testDrive' ? 'Test Drive Request' : 
                    activeForm === 'offer' ? 'Car Offer' : 'Pre-Approval Request',
              fullName: formData.fullName, 
              email: formData.email,
              phone: formData.phone,
              
              // Details section
              message: activeForm === 'testDrive' 
                  ? `Requesting Test Drive on ${formData.date} at ${formData.time}` 
                  : (formData.comments || "No additional comments"),
              
              offerAmount: formData.offerAmount ? `${currencySymbol}${formData.offerAmount}` : "N/A",
              
              carName: `${car.year} ${car.make} ${car.model}`,
              
              itemName: "N/A", // This is for general goods, not cars
              
              annualIncome: formData.annualIncome ? `${currencySymbol}${formData.annualIncome}` : "N/A"
          };

          // 3. Send Email
          await emailjs.send(
              'service_nkj2h8t', 
              'template_lih1iy7', 
              emailParams, 
              'MpvXLl-LNdIwPQyBf'
          );

          if (collectionName === 'preApprovals') {
             setApprovalStatus('Pending'); 
          }

          setFormStatus('Success! Request sent to Admin.');
          setTimeout(() => {
              setActiveForm(null);
              setFormStatus('');
          }, 3000);
      } catch (error) {
          console.error(error);
          setFormStatus('Error submitting form.');
      }
  };

  // --- Animation Hooks ---
  const [headerRef, headerVisible] = useScrollReveal(0.1, !loading);
  const [leftRef, leftVisible] = useScrollReveal(0.1, !loading);
  const [rightRef, rightVisible] = useScrollReveal(0.1, !loading);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const getStyle = (isVisible, transformStart, delay = '0s') => ({
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translate(0, 0)' : transformStart,
      transition: `opacity 0.8s ease-out ${delay}, transform 0.8s ease-out ${delay}`
  });

  if (loading) return <div className="container" style={{padding: '100px 20px', textAlign: 'center'}}><h2>Loading...</h2></div>;
  if (error || !car) return <div className="container" style={{padding: '100px 20px', textAlign: 'center'}}><h2>{error || "Not Found"}</h2></div>;

  const currencySymbol = car.currency || '$';
  const displayPrice = car.price ? `${currencySymbol}${Number(car.price).toLocaleString()}` : 'Call for Price';
  const displayMileage = car.mileage ? Number(car.mileage).toLocaleString() : 'Unknown';
  const currentImage = selectedImage || 'https://via.placeholder.com/600x400?text=No+Image';
  const isSold = car.status === 'Sold';

  return (
    <div className="container" style={{padding: '40px 20px', overflowX: 'hidden'}}>
      
      <div ref={headerRef} style={{...getStyle(headerVisible, 'translateY(-20px)'), display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
          <Link to="/" style={{display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: '#333', fontWeight: 'bold'}}><FaHome /> Back to Home</Link>
          <Link to="/inventory" style={{display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: '#333', fontWeight: 'bold'}}><FaArrowLeft /> Go to Inventory</Link>
      </div>
      
      <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '40px' }}>
        
        {/* Images Section */}
        <div ref={leftRef} style={{flex: '1', minWidth: '300px', ...getStyle(leftVisible, 'translateX(-50px)', '0.2s')}}>
            <div style={{overflow: 'hidden', borderRadius: '8px', border: '1px solid #eee', marginBottom: '10px', position: 'relative'}}>
                <img src={currentImage} alt={car.model} style={{width: '100%', height: 'auto', display: 'block', objectFit: 'cover', aspectRatio: '4/3'}} />
                {isSold && (
                    <div style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', 
                        background: 'rgba(255,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontSize: '3rem', fontWeight: 'bold', textShadow: '2px 2px 4px black',
                        animation: 'blink 2s infinite'
                    }}>SOLD OUT</div>
                )}
            </div>
            {car.images && car.images.length > 1 && (
              <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '5px' }}>
                {car.images.map((img, index) => (
                  <img key={index} src={img} alt={`Thumbnail ${index}`} onClick={() => setSelectedImage(img)}
                    style={{
                      width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer',
                      border: selectedImage === img ? '2px solid #007bff' : '2px solid transparent',
                      opacity: selectedImage === img ? 1 : 0.6
                    }}
                  />
                ))}
              </div>
            )}
        </div>

        {/* Details Section */}
        <div ref={rightRef} style={{flex: '1', minWidth: '300px', ...getStyle(rightVisible, 'translateX(50px)', '0.4s')}}>
            <h1 style={{fontSize: '2.5rem', margin: '0'}}>{car.year} {car.make} {car.model}</h1>
            <h2 style={{color: '#007bff', fontSize: '2rem', margin: '10px 0'}}>{displayPrice}</h2>
            
            {/* Action Buttons */}
            {!isSold ? (
                <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap', margin: '20px 0'}}>
                    <button className="btn btn-primary" onClick={() => handleActionClick('testDrive')} style={{flex: '1', minWidth: '150px'}}>Schedule Test Drive</button>
                    <button className="btn btn-outline" onClick={() => handleActionClick('offer')} style={{flex: '1', minWidth: '150px'}}>Make an Offer</button>
                    
                    {approvalStatus === 'Approved' ? (
                         <div style={{
                             flex: '1', minWidth: '200px', padding: '12px 24px', 
                             background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb', 
                             borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: 'bold'
                         }}>
                             <FaCheckCircle /> Approved
                         </div>
                    ) : approvalStatus === 'Pending' ? (
                        <div style={{
                             flex: '1', minWidth: '200px', padding: '12px 24px', 
                             background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', 
                             borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: 'bold'
                         }}>
                             <FaClock /> Pending Approval
                         </div>
                    ) : (
                        <button className="btn btn-outline" onClick={() => handleActionClick('preApproval')} style={{color:'#28a745', borderColor:'#28a745', flex: '1', minWidth: '200px'}}>
                            <FaFileContract /> Get Pre-Approved
                        </button>
                    )}
                </div>
            ) : (
                <div style={{padding: '20px', background: '#ffebeb', border: '1px solid red', color: 'red', fontWeight: 'bold', textAlign: 'center', borderRadius: '5px', margin: '20px 0'}}>
                    This vehicle has been sold.
                </div>
            )}

            {/* Inline Forms */}
            {activeForm && (
                <div style={{background: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '20px'}}>
                    <h4 style={{textTransform: 'capitalize'}}>{activeForm.replace(/([A-Z])/g, ' $1').trim()} Form</h4>
                    {formStatus && <p style={{fontWeight: 'bold', color: formStatus.includes('Success') ? 'green' : 'red'}}>{formStatus}</p>}
                    
                    {/* ADDED: Common Compulsory Fields */}
                    <div style={{marginBottom:'15px', borderBottom:'1px solid #ddd', paddingBottom:'10px'}}>
                        <input type="text" name="fullName" placeholder="Full Name *" required value={formData.fullName} onChange={handleFormChange} style={{width:'100%', marginBottom:'10px', padding:'8px'}} />
                        <input type="email" name="email" placeholder="Email Address *" required value={formData.email} onChange={handleFormChange} style={{width:'100%', marginBottom:'10px', padding:'8px'}} />
                        <input type="tel" name="phone" placeholder="Phone Number *" required value={formData.phone} onChange={handleFormChange} style={{width:'100%', padding:'8px'}} />
                    </div>

                    {activeForm === 'testDrive' && (
                        <form onSubmit={(e) => handleFormSubmit(e, 'testDrives')}>
                            <label style={{display:'block', marginBottom:'5px', fontWeight:'bold'}}>Preferred Date</label>
                            <input type="date" name="date" required value={formData.date} onChange={handleFormChange} style={{width:'100%', marginBottom:'10px', padding:'8px'}} />
                            <label style={{display:'block', marginBottom:'5px', fontWeight:'bold'}}>Preferred Time</label>
                            <input type="time" name="time" required value={formData.time} onChange={handleFormChange} style={{width:'100%', marginBottom:'15px', padding:'8px'}} />
                            <button type="submit" className="btn btn-primary" style={{width:'100%'}}>Request Test Drive</button>
                        </form>
                    )}
                    
                    {activeForm === 'offer' && (
                        <form onSubmit={(e) => handleFormSubmit(e, 'offers')}>
                            <input type="number" name="offerAmount" placeholder={`Your Offer (${currencySymbol}) *`} required value={formData.offerAmount} onChange={handleFormChange} style={{width:'100%', marginBottom:'10px', padding:'8px'}} />
                            <textarea name="comments" placeholder="Additional Comments" rows="3" value={formData.comments} onChange={handleFormChange} style={{width:'100%', marginBottom:'15px', padding:'8px'}}></textarea>
                            <button type="submit" className="btn btn-primary" style={{width:'100%'}}>Submit Offer</button>
                        </form>
                    )}

                    {activeForm === 'preApproval' && (
                         <form onSubmit={(e) => handleFormSubmit(e, 'preApprovals')}>
                            <input type="number" name="annualIncome" placeholder={`Annual Income (${currencySymbol}) *`} required value={formData.annualIncome} onChange={handleFormChange} style={{width:'100%', marginBottom:'10px', padding:'8px'}} />
                            <input type="text" name="employmentStatus" placeholder="Employment Status *" required value={formData.employmentStatus} onChange={handleFormChange} style={{width:'100%', marginBottom:'10px', padding:'8px'}} />
                             <input type="number" name="creditScore" placeholder="Estimated Credit Score" value={formData.creditScore} onChange={handleFormChange} style={{width:'100%', marginBottom:'15px', padding:'8px'}} />
                            <button type="submit" className="btn btn-primary" style={{width:'100%'}}>Submit Application</button>
                        </form>
                    )}
                    <button onClick={() => setActiveForm(null)} style={{marginTop:'10px', background:'none', border:'none', color:'#666', cursor:'pointer', width:'100%'}}>Cancel</button>
                </div>
            )}

            <div style={{background: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ddd'}}>
                <h3>Vehicle Highlights</h3>
                <ul style={{listStyle: 'none', padding: 0, marginTop: '10px', lineHeight: '1.8'}}>
                    <li><strong>Mileage:</strong> {displayMileage} miles</li>
                    <li style={{display: 'flex', alignItems: 'center', gap: '8px'}}> <FaCar style={{color: '#666'}} /> <strong>Body Type:</strong> {car.bodyType || 'N/A'}</li>
                    <li><strong>Status:</strong> {car.status || 'N/A'}</li>
                    <li><strong>VIN:</strong> {car.vin || 'N/A'}</li>
                </ul>
            </div>
            <div style={{marginTop: '20px'}}>
                <PaymentCalculator price={car.price ? Number(car.price) : 0} currency={currencySymbol} />
            </div>
        </div>
      </div>
      <style>{`@keyframes blink { 50% { opacity: 0.5; } }`}</style>
    </div>
  );
};

export default VehicleDetails;